// src/pages/Lessons.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCart } from '../context/CartContext';
import { LESSON_CATEGORY } from '../catalog';

const fetchPieces = async () => {
  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
  if (!apiEndpoint) {
    return [];
  }

  const response = await fetch(`${apiEndpoint}pieces`);
  if (!response.ok) {
    throw new Error('Failed to fetch catalog');
  }
  return response.json();
};

const Lessons = () => {
  // Lessons are sold as ordinary catalog items, not Square Appointments:
  // addProduct keeps them out of the /bookings call, which requires an
  // Appointments-capable subscription the shop doesn't have yet.
  const { addProduct } = useCart();
  const { data: pieces, isLoading, isError } = useQuery({
    queryKey: ['pieces'],
    queryFn: fetchPieces
  });

  const lessons = pieces?.filter((piece) => piece.category === LESSON_CATEGORY);

  return (
    <div className="container mx-auto px-4 pt-20 pb-8">
      <div className="max-w-3xl">
        <h1 className="font-display text-4xl font-semibold text-charcoal-900">Lessons</h1>
        <p className="mt-4 text-walnut-600">
          Spend a half or full day in the shop working alongside John on a project of your
          own. Lessons suit complete beginners as readily as woodworkers looking to sharpen
          a specific skill.
        </p>
        <div className="mt-6 rounded-lg border border-linen-200 bg-linen-100 p-4">
          <h2 className="font-display text-lg font-semibold text-charcoal-900">
            How scheduling works
          </h2>
          <p className="mt-2 text-walnut-600">
            Reserve your lesson below and we'll email you within a day or two to settle on a
            date, a time, and how many people are coming. Nothing is locked to the calendar
            until we've both agreed on it — if we can't find a date that works, we'll refund
            you in full.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-10">Loading...</div>
      ) : isError ? (
        <div className="mt-10 text-walnut-600">
          We couldn't load lessons just now. Please{' '}
          <Link to="/contact" className="text-copper-600 underline">
            get in touch
          </Link>{' '}
          and we'll get you booked.
        </div>
      ) : lessons?.length ? (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lessons.map((lesson) => (
            <div
              key={lesson.id}
              className="bg-linen-50 rounded-lg shadow-md shadow-walnut-900/5 border border-linen-200 overflow-hidden"
            >
              {lesson.imageUrl ? (
                <img
                  src={lesson.imageUrl}
                  alt={lesson.title}
                  className="w-full h-64 object-cover"
                />
              ) : (
                <div className="w-full h-64 flex items-center justify-center bg-linen-100 text-walnut-400">
                  Photo coming soon
                </div>
              )}
              <div className="p-4">
                <h3 className="font-display text-xl font-semibold text-charcoal-900">
                  {lesson.title}
                </h3>
                <p className="text-walnut-600">{lesson.description}</p>
                <p className="text-copper-600 font-bold mt-2">
                  {lesson.price != null ? `$${lesson.price}` : 'Inquire for price'}
                </p>
                {lesson.available && lesson.price != null && (
                  <button
                    onClick={() => addProduct(lesson)}
                    className="mt-4 w-full bg-copper-500 text-linen-50 px-4 py-2 rounded-lg hover:bg-copper-600 transition-colors"
                  >
                    Reserve this lesson
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-10 text-walnut-600">
          Lesson dates aren't posted online yet. Please{' '}
          <Link to="/contact" className="text-copper-600 underline">
            get in touch
          </Link>{' '}
          and we'll arrange one with you directly.
        </div>
      )}
    </div>
  );
};

export default Lessons;
